import React from 'react';

// Accent color presets
const ACCENT_COLORS = {
    purple: {
        bg: 'bg-[#7B61FF]/20',
        border: 'border-[#7B61FF]',
        shadow: 'shadow-[0_0_20px_rgba(123,97,255,0.3)]',
        gradient: 'from-[#7B61FF]/20',
        iconBg: 'bg-[#7B61FF]',
        checkBorder: 'border-[#7B61FF]',
        checkBg: 'bg-[#7B61FF]',
    },
    orange: {
        bg: 'bg-[#F6821F]/20',
        border: 'border-[#F6821F]',
        shadow: 'shadow-[0_0_20px_rgba(246,130,31,0.3)]',
        gradient: 'from-[#F6821F]/20',
        iconBg: 'bg-[#F6821F]',
        checkBorder: 'border-[#F6821F]',
        checkBg: 'bg-[#F6821F]',
    },
};

export default function VisualOptionGroup({ options, value, onChange, multi = false, columns = 2, accentColor = 'purple' }) {
    const colors = ACCENT_COLORS[accentColor] || ACCENT_COLORS.purple;

    const handleSelect = (id, disabled) => {
        if (disabled) return;

        if (multi) {
            const current = Array.isArray(value) ? value : [];
            const next = current.includes(id)
                ? current.filter(item => item !== id)
                : [...current, id];
            onChange(next);
        } else {
            onChange(id);
        }
    };

    const getGridClass = () => {
        switch (columns) {
            case 1: return 'grid-cols-1';
            case 2: return 'grid-cols-1 md:grid-cols-2';
            case 3: return 'grid-cols-1 md:grid-cols-3';
            case 4: return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
            default: return 'grid-cols-1 md:grid-cols-2';
        }
    };

    return (
        <div className={`grid ${getGridClass()} gap-4`}>
            {options.map((option) => {
                const isSelected = multi
                    ? (Array.isArray(value) && value.includes(option.id))
                    : value === option.id;
                const isDisabled = option.disabled;

                return (
                    <div
                        key={option.id}
                        onClick={() => handleSelect(option.id, isDisabled)}
                        className={`
                            group relative overflow-hidden rounded-xl p-6 transition-all duration-300
                            border backdrop-blur-md
                            ${isDisabled
                                ? 'bg-white/[0.02] border-white/5 cursor-not-allowed opacity-50'
                                : isSelected
                                    ? `${colors.bg} ${colors.border} ${colors.shadow} cursor-pointer`
                                    : 'bg-white/[0.03] border-white/10 hover:bg-white/[0.06] hover:border-white/20 cursor-pointer'
                            }
                        `}
                    >
                        {/* Badge (e.g., Coming Soon) */}
                        {option.badge && (
                            <div className="absolute top-3 right-3 z-20">
                                <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-white text-gray-800">
                                    {option.badge}
                                </span>
                            </div>
                        )}

                        {/* Interactive Glow Background */}
                        {isSelected && !isDisabled && (
                            <div className={`absolute inset-0 bg-gradient-to-br ${colors.gradient} via-transparent to-transparent opacity-50`} />
                        )}

                        <div className="relative z-10 flex items-start space-x-4">
                            {/* Optional Icon */}
                            {option.icon && (
                                <div className={`
                                    p-3 rounded-lg transition-colors duration-300
                                    ${isDisabled
                                        ? 'bg-white/5 text-gray-500'
                                        : isSelected
                                            ? `${colors.iconBg} text-white`
                                            : 'bg-white/5 text-gray-400 group-hover:text-white'
                                    }
                                `}>
                                    {option.icon}
                                </div>
                            )}

                            <div className="flex-1">
                                <h3 className={`font-semibold mb-1 transition-colors ${
                                    isDisabled
                                        ? 'text-gray-500'
                                        : isSelected
                                            ? 'text-white'
                                            : 'text-gray-200 group-hover:text-white'
                                }`}>
                                    {option.label}
                                </h3>
                                {option.description && (
                                    <p className={`text-sm leading-relaxed ${isDisabled ? 'text-gray-600' : 'text-gray-400'}`}>
                                        {option.description}
                                    </p>
                                )}
                            </div>

                            {/* Selection Indicator - hidden for disabled */}
                            {!isDisabled && (
                                <div className={`
                                    w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300
                                    ${isSelected
                                        ? `${colors.checkBorder} ${colors.checkBg} scale-110`
                                        : 'border-white/20 group-hover:border-white/40'
                                    }
                                `}>
                                    {isSelected && (
                                        <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
